import { useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import { bookingLinkProps } from "@/helpers/url";
import useAnalytics from "@/hooks/use-analytics";
import { cn } from "@/lib/utils";

import { GameStatusEnum, type GameType } from "../types";
import BookNowButton from "./book-now-button";
import WebsiteButton from "./website-button";

interface BookButtonProps {
  className?: string;
  game: GameType;
}

export default function BookButton({ className, game }: BookButtonProps) {
  const [hideButton, setHideButton] = useState(false);
  const search = useSearch({ strict: false });

  const { track } = useAnalytics();

  const handleClick = () => {
    track("Booking Button Tapped", {
      company_id: game.location.company.id,
      company_name: game.location.company.name,
      game_id: game.id,
      game_name: game.name,
      is_bookable: game.hasBookings,
      location_id: game.location.id,
      search_results_position: (search as Record<string, unknown>).s || 0,
    });
  };

  const handleScroll = () => {
    setHideButton(
      window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 600
    );
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: trust me
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: its fine
  const button = useMemo(() => {
    if (
      game.status === GameStatusEnum.PC ||
      game.status === GameStatusEnum.TC
    ) {
      return null;
    }

    if (game.hasBookings) {
      return <BookNowButton game={game} onClick={handleClick} />;
    }

    const url = bookingLinkProps(game);

    return url ? <WebsiteButton onClick={handleClick} {...url} /> : null;
  }, [game]);

  if (!button) {
    return null;
  }

  return (
    <div
      className={cn(
        "fixed inset-x-0 bottom-0 z-10 bg-eerie-black px-[25px] py-[15px] md:relative md:bg-transparent md:p-0",
        hideButton && "opacity-0 md:opacity-100",
        className
      )}
    >
      {button}
    </div>
  );
}
